import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SlideshowOutlinedIcon from "@mui/icons-material/SlideshowOutlined";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { useTranslation } from "react-i18next";
import { pygHeroShellSx } from "../PyGAnalitico/PygAnaliticoPageHeader";
import {
  buildCrearMensajesState,
  getConclusionBloques,
  getIntroOpciones,
} from "./crearMensajesHelpers";
import { exportCrearMensajesPdf } from "./exportCrearMensajesPdf";
import { FormattedText } from "./crearMensajesFormatting";
import PanelLateralCrearMensajes from "./PanelLateralCrearMensajes";
import PresentacionCrearMensajesDialog from "./PresentacionCrearMensajesDialog";

const INTRO_META = {
  preguntas: { color: "warning", icon: "?" },
  dato: { color: "error", icon: "%" },
  storytelling: { color: "info", icon: "✦" },
};

const MENSAJE_META = [
  { emoji: "☝️", accent: "primary", step: 1 },
  { emoji: "✌️", accent: "secondary", step: 2 },
  { emoji: "🤟", accent: "info", step: 3 },
];

const fieldSx = (theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    bgcolor: alpha(theme.palette.background.paper, 0.9),
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.primary.main, 0.4),
    },
  },
  "& .MuiInputBase-root": { alignItems: "flex-start" },
});

const displayFieldSx = (theme, minRows) => ({
  borderRadius: 2,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.55),
  bgcolor: alpha(theme.palette.background.paper, 0.9),
  px: 1.5,
  py: 1.25,
  minHeight: minRows * 22,
  cursor: "text",
  transition: "border-color 0.2s ease",
  "&:hover": {
    borderColor: alpha(theme.palette.primary.main, 0.45),
  },
});

const EditableBlock = ({
  value,
  onChange,
  minRows = 6,
  label,
  theme,
  editHint,
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  if (editing) {
    return (
      <TextField
        inputRef={inputRef}
        fullWidth
        multiline
        minRows={minRows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        label={label}
        variant="outlined"
        size="small"
        sx={fieldSx(theme)}
      />
    );
  }

  return (
    <Box>
      {label ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.5, ml: 0.25, fontWeight: 500 }}
        >
          {label}
        </Typography>
      ) : null}
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setEditing(true);
          }
        }}
        sx={displayFieldSx(theme, minRows)}
      >
        <FormattedText text={value} variant="body2" />
      </Box>
      {editHint ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, ml: 0.25 }}
        >
          {editHint}
        </Typography>
      ) : null}
    </Box>
  );
};

const TimelineNode = ({ label, emoji, accent, isLast, theme }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: { xs: 56, sm: 148 },
      flexShrink: 0,
    }}
  >
    <Paper
      elevation={0}
      sx={{
        px: { xs: 0.5, sm: 1.5 },
        py: 1,
        borderRadius: 2.5,
        border: "2px solid",
        borderColor: alpha(accent, 0.45),
        bgcolor: alpha(accent, 0.08),
        textAlign: "center",
        width: "100%",
        boxShadow: `0 4px 16px ${alpha(accent, 0.12)}`,
      }}
    >
      {emoji ? (
        <Typography
          sx={{ fontSize: { xs: 20, sm: 22 }, lineHeight: 1, mb: 0.25 }}
        >
          {emoji}
        </Typography>
      ) : null}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          lineHeight: 1.25,
          display: { xs: "none", sm: "block" },
          color: accent,
          letterSpacing: -0.1,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          display: { xs: "block", sm: "none" },
          fontSize: "0.65rem",
          color: accent,
        }}
      >
        {label.split(" ")[0]}
      </Typography>
    </Paper>
    {!isLast ? (
      <Box
        sx={{
          width: 3,
          flex: 1,
          minHeight: 28,
          borderRadius: 2,
          background: `linear-gradient(180deg, ${accent}, ${alpha(theme.palette.divider, 0.5)})`,
          my: 0.75,
        }}
      />
    ) : null}
  </Box>
);

const SectionRow = ({
  label,
  emoji,
  accent,
  children,
  isLast = false,
  subtitle,
}) => {
  const theme = useTheme();
  const color = accent || theme.palette.primary.main;

  return (
    <Box
      sx={{
        display: "flex",
        gap: { xs: 1, sm: 2 },
        alignItems: "stretch",
        mb: isLast ? 0 : 1,
      }}
    >
      <TimelineNode
        label={label}
        emoji={emoji}
        accent={color}
        isLast={isLast}
        theme={theme}
      />
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: { xs: 1.5, sm: 2 },
          mb: isLast ? 0 : 2,
          border: "1px solid",
          borderColor: alpha(color, 0.22),
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.paper, 0.96),
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(160deg, ${alpha(color, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 50%)`
              : `linear-gradient(160deg, ${alpha(color, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.99)} 55%)`,
          boxShadow: `0 8px 32px ${alpha(color, 0.06)}`,
          minWidth: 0,
        }}
      >
        {subtitle ? (
          <Chip
            size="small"
            variant="outlined"
            label={subtitle}
            sx={{
              mb: 1.5,
              height: 22,
              fontWeight: 600,
              fontSize: "0.75rem",
              borderColor: alpha(color, 0.35),
              bgcolor: alpha(color, 0.04),
            }}
          />
        ) : null}
        {children}
      </Paper>
    </Box>
  );
};

const PaginaCrearMensajes = () => {
  const theme = useTheme();
  const { contentMargin } = useContext(SidebarContext);
  const { t, i18n } = useTranslation();
  const accent = theme.palette.primary.main;

  const introOpciones = useMemo(() => getIntroOpciones(t), [t]);
  const conclusionBloques = useMemo(() => getConclusionBloques(t), [t]);
  const defaults = useMemo(() => buildCrearMensajesState(t), [t]);

  const [introTipo, setIntroTipo] = useState("storytelling");
  const [introTextos, setIntroTextos] = useState(defaults.introTextos);
  const [collar, setCollar] = useState(defaults.collar);
  const [mensaje1Titulo, setMensaje1Titulo] = useState(defaults.mensaje1Titulo);
  const [mensaje1, setMensaje1] = useState(defaults.mensaje1);
  const [mensaje2Titulo, setMensaje2Titulo] = useState(defaults.mensaje2Titulo);
  const [mensaje2, setMensaje2] = useState(defaults.mensaje2);
  const [mensaje3Titulo, setMensaje3Titulo] = useState(defaults.mensaje3Titulo);
  const [mensaje3, setMensaje3] = useState(defaults.mensaje3);
  const [conclusionAntesDespues, setConclusionAntesDespues] = useState(
    defaults.conclusionAntesDespues,
  );
  const [conclusionRecordatorio, setConclusionRecordatorio] = useState(
    defaults.conclusionRecordatorio,
  );
  const [conclusionLlamadaAccion, setConclusionLlamadaAccion] = useState(
    defaults.conclusionLlamadaAccion,
  );
  const [presentacionOpen, setPresentacionOpen] = useState(false);

  useEffect(() => {
    setIntroTextos(defaults.introTextos);
    setCollar(defaults.collar);
    setMensaje1Titulo(defaults.mensaje1Titulo);
    setMensaje1(defaults.mensaje1);
    setMensaje2Titulo(defaults.mensaje2Titulo);
    setMensaje2(defaults.mensaje2);
    setMensaje3Titulo(defaults.mensaje3Titulo);
    setMensaje3(defaults.mensaje3);
    setConclusionAntesDespues(defaults.conclusionAntesDespues);
    setConclusionRecordatorio(defaults.conclusionRecordatorio);
    setConclusionLlamadaAccion(defaults.conclusionLlamadaAccion);
  }, [defaults]);

  const updateIntroTexto = (tipo, texto) => {
    setIntroTextos((prev) => ({ ...prev, [tipo]: texto }));
  };

  const mensajes = [
    {
      titulo: mensaje1Titulo,
      setTitulo: setMensaje1Titulo,
      cuerpo: mensaje1,
      setCuerpo: setMensaje1,
      meta: MENSAJE_META[0],
    },
    {
      titulo: mensaje2Titulo,
      setTitulo: setMensaje2Titulo,
      cuerpo: mensaje2,
      setCuerpo: setMensaje2,
      meta: MENSAJE_META[1],
    },
    {
      titulo: mensaje3Titulo,
      setTitulo: setMensaje3Titulo,
      cuerpo: mensaje3,
      setCuerpo: setMensaje3,
      meta: MENSAJE_META[2],
    },
  ];

  const conclusionValues = {
    antesDespues: conclusionAntesDespues,
    recordatorio: conclusionRecordatorio,
    llamadaAccion: conclusionLlamadaAccion,
  };

  const conclusionSetters = {
    antesDespues: setConclusionAntesDespues,
    recordatorio: setConclusionRecordatorio,
    llamadaAccion: setConclusionLlamadaAccion,
  };

  const editHint = t("crearMensajes.clickToEdit");

  const handleExportPdf = () => {
    exportCrearMensajesPdf({
      t,
      introTipo,
      introTextos,
      introOpciones,
      collar,
      mensajes: mensajes.map((msg) => ({
        step: msg.meta.step,
        titulo: msg.titulo,
        cuerpo: msg.cuerpo,
      })),
      conclusionBloques,
      conclusionValues,
    });
  };

  return (
    <Box
      key={i18n.language}
      sx={{ p: { xs: 1.25, sm: 2 }, ...contentMargin, pb: 6 }}
    >
      <Box sx={pygHeroShellSx(theme, { sticky: true })}>
        <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
          <Stack spacing={1.25}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "flex-start" }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="flex-start"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(accent, 0.16),
                    color: theme.palette.primary.dark,
                    border: "1px solid",
                    borderColor: alpha(accent, 0.28),
                  }}
                >
                  <CampaignOutlinedIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ fontWeight: 700, letterSpacing: 1.1 }}
                  >
                    {t("crearMensajes.overline")}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, lineHeight: 1.1 }}
                  >
                    {t("sidebar.CrearMensajes")}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mt: 0.75 }}
                  >
                    <Chip
                      size="small"
                      icon={
                        <RecordVoiceOverOutlinedIcon
                          sx={{ fontSize: "16px !important" }}
                        />
                      }
                      label={t("crearMensajes.flowChip")}
                      sx={{ fontWeight: 700, bgcolor: alpha(accent, 0.08) }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("crearMensajes.editableChip")}
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                </Box>
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{
                  alignSelf: { xs: "stretch", md: "center" },
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<SlideshowOutlinedIcon />}
                  onClick={() => setPresentacionOpen(true)}
                  sx={{
                    fontWeight: 700,
                    color: "red",
                    border: "1px solid red",
                  }}
                >
                  {t("crearMensajes.presentation.viewButton")}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleExportPdf}
                  sx={{
                    fontWeight: 700,
                    boxShadow: `0 4px 14px ${alpha(accent, 0.28)}`,
                  }}
                >
                  {t("crearMensajes.downloadPDF")}
                </Button>
              </Stack>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ pl: 0.25 }}
            >
              {t("crearMensajes.description")}{" "}
              {t("crearMensajes.formattingHint")}
            </Typography>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ display: { xs: "block", lg: "none" }, mb: 2 }}>
        <PanelLateralCrearMensajes />
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: { xs: 0, lg: 2 },
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SectionRow
            label={t("crearMensajes.sections.intro.label")}
            accent={theme.palette.warning.main}
            subtitle={t("crearMensajes.sections.intro.subtitle")}
          >
            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              flexWrap="wrap"
              sx={{ mb: 2 }}
            >
              {Object.entries(introOpciones).map(([key, opcion]) => {
                const meta = INTRO_META[key];
                const selected = introTipo === key;
                const main = theme.palette[meta.color].main;
                return (
                  <Chip
                    key={key}
                    label={opcion.label}
                    clickable
                    onClick={() => setIntroTipo(key)}
                    variant={selected ? "filled" : "outlined"}
                    color={meta.color}
                    sx={{
                      fontWeight: 700,
                      height: 32,
                      transition: "all 0.2s ease",
                      ...(selected
                        ? { boxShadow: `0 4px 14px ${alpha(main, 0.35)}` }
                        : {
                            bgcolor: alpha(main, 0.06),
                            borderColor: alpha(main, 0.3),
                            color: main,
                          }),
                    }}
                  />
                );
              })}
            </Stack>

            <Stack spacing={2}>
              {Object.entries(introOpciones).map(([key, opcion], index) => {
                const meta = INTRO_META[key];
                const selected = introTipo === key;
                const main = theme.palette[meta.color].main;
                const isStory = key === "storytelling";

                return (
                  <Box key={key}>
                    {index > 0 ? (
                      <Chip
                        label={t("crearMensajes.introOr")}
                        size="small"
                        sx={{
                          mb: 1,
                          fontWeight: 800,
                          height: 22,
                          bgcolor: selected ? alpha(main, 0.15) : undefined,
                        }}
                      />
                    ) : null}
                    <Paper
                      elevation={0}
                      onClick={() => setIntroTipo(key)}
                      sx={{
                        p: 1.5,
                        cursor: "pointer",
                        borderRadius: 2.5,
                        border: "2px solid",
                        borderColor: selected
                          ? isStory
                            ? theme.palette.info.main
                            : main
                          : alpha(theme.palette.divider, 0.6),
                        bgcolor: selected
                          ? alpha(main, 0.06)
                          : alpha(theme.palette.action.hover, 0.3),
                        transition: "all 0.2s ease",
                        opacity: selected ? 1 : 0.72,
                        "&:hover": {
                          opacity: 1,
                          borderColor: alpha(main, 0.5),
                          boxShadow: `0 6px 20px ${alpha(main, 0.1)}`,
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          display: "block",
                          color: main,
                        }}
                      >
                        {opcion.label}
                      </Typography>
                      <EditableBlock
                        value={introTextos[key]}
                        onChange={(v) => updateIntroTexto(key, v)}
                        minRows={key === "storytelling" ? 14 : 3}
                        theme={theme}
                        editHint={editHint}
                      />
                    </Paper>
                  </Box>
                );
              })}
            </Stack>
          </SectionRow>

          <SectionRow
            label={t("crearMensajes.sections.collar.label")}
            accent={theme.palette.secondary.main}
            subtitle={t("crearMensajes.sections.collar.subtitle")}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <LinkOutlinedIcon
                sx={{ color: theme.palette.secondary.main, fontSize: 20 }}
              />
              <Typography variant="body2" color="text.secondary">
                {t("crearMensajes.sections.collar.hint")}
              </Typography>
            </Stack>
            <EditableBlock
              value={collar}
              onChange={setCollar}
              minRows={4}
              theme={theme}
              editHint={editHint}
            />
          </SectionRow>

          {mensajes.map((msg) => {
            const paletteColor = theme.palette[msg.meta.accent].main;
            return (
              <SectionRow
                key={msg.meta.step}
                label={t("crearMensajes.sections.mensaje.label", {
                  step: msg.meta.step,
                })}
                emoji={msg.meta.emoji}
                accent={paletteColor}
              >
                <TextField
                  fullWidth
                  value={msg.titulo}
                  onChange={(e) => msg.setTitulo(e.target.value)}
                  label={t("crearMensajes.fields.messageTitle")}
                  variant="outlined"
                  size="small"
                  sx={{
                    mb: 2,
                    ...fieldSx(theme),
                    "& input": { fontWeight: 800, fontSize: "1rem" },
                  }}
                />
                <EditableBlock
                  value={msg.cuerpo}
                  onChange={msg.setCuerpo}
                  minRows={12}
                  label={t("crearMensajes.fields.messageBody")}
                  theme={theme}
                  editHint={editHint}
                />
              </SectionRow>
            );
          })}

          <SectionRow
            label={t("crearMensajes.sections.conclusion.label")}
            accent={theme.palette.success.main}
            isLast
            subtitle={t("crearMensajes.sections.conclusion.subtitle")}
          >
            <Stack spacing={2}>
              {conclusionBloques.map((bloque) => {
                const main = theme.palette[bloque.color].main;
                return (
                  <Paper
                    key={bloque.key}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      border: "1px solid",
                      borderColor: alpha(main, 0.28),
                      borderLeft: `4px solid ${main}`,
                      bgcolor: alpha(main, 0.03),
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 0.5 }}
                    >
                      <FlagOutlinedIcon sx={{ fontSize: 18, color: main }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {bloque.title}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1.25 }}
                    >
                      {bloque.hint}
                    </Typography>
                    <EditableBlock
                      value={conclusionValues[bloque.key]}
                      onChange={conclusionSetters[bloque.key]}
                      minRows={
                        bloque.key === "antesDespues"
                          ? 14
                          : bloque.key === "llamadaAccion"
                            ? 16
                            : 8
                      }
                      theme={theme}
                      editHint={editHint}
                    />
                  </Paper>
                );
              })}
            </Stack>
          </SectionRow>
        </Box>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: 640,
            flexShrink: 0,
            position: "sticky",
            top: 12,
            alignSelf: "flex-start",
            maxHeight: "calc(100vh - 24px)",
            overflowY: "auto",
            pr: 0.5,
          }}
        >
          <PanelLateralCrearMensajes />
        </Box>
      </Box>

      <PresentacionCrearMensajesDialog
        open={presentacionOpen}
        onClose={() => setPresentacionOpen(false)}
      />
    </Box>
  );
};

export default PaginaCrearMensajes;
